// MISE À JOUR AUTOMATIQUE DE L'APP (26/09/2026, avant le test en boutique).
//
// LE PROBLÈME. Le service worker (src/sw.js) s'active tout de suite
// (skipWaiting + clientsClaim), mais la page déjà ouverte a été servie depuis
// l'ANCIEN cache : le joueur ne voyait la nouvelle version qu'au lancement
// d'APRÈS. Et une PWA reprise depuis l'arrière-plan ne recharge rien et ne
// vérifie aucune mise à jour : un enfant pouvait garder une version périmée
// pendant des jours.
//
// LA RÈGLE. On vérifie à chaque retour dans l'app (et toutes les 30 min). Dès
// qu'une nouvelle version a pris la main, on recharge au premier moment sûr :
//   · à la prochaine NAVIGATION, vers l'écran demandé. Obligatoire, pas un
//     confort : tous les écrans sont chargés à la demande, et une fois la
//     nouvelle version en place, ceux de l'ancienne n'existent plus (constaté
//     au test : « Couldn't resolve component »). Seule exception : d'un
//     chapitre à l'autre dans le lecteur, dont le code est déjà là ;
//   · au RETOUR dans l'app, sauf si l'enfant était dans une histoire, un scan
//     ou la caméra : on ne coupe jamais une lecture en cours.
// Filet : si un écran ne se charge pas malgré tout (déploiement pas encore vu
// par le service worker), on recharge vers cet écran, une fois.
//
// POURQUOI PAS registerType « autoUpdate » SEUL. Avec lui, le plugin recharge
// la page dès que la nouvelle version prend la main, où qu'en soit l'enfant,
// y compris au milieu d'une lecture. On enregistre donc le service worker
// nous-mêmes (injectRegister: null dans vite.config.mjs), avec l'API native :
// workbox-window ne signale PAS une mise à jour trouvée plus d'une minute
// après l'enregistrement (il la tient pour « externe »), c'est-à-dire le cas
// réel d'une app ouverte depuis un moment (constaté au test du 26/09).

export const ROUTES_PROTEGEES = new Set(["histoire-route", "scan-result-route", "scanner-route"]);
// 07/10/2026 (bug A12, téléphone de Benjamin) : la page s'ouvrait sur l'écran
// de scan avec l'ANCIENNE version, et la nouvelle, arrivée 2 s plus tard,
// n'était jamais appliquée (écran protégé, aucune navigation) : chargement
// sans fin. Juste après l'ouverture, l'enfant n'a encore rien fait : on
// recharge tout de suite, sauf en pleine histoire. Un scan en cours ne
// risque rien (son lot d'écritures est atomique ; rejoué, il lit l'état).
export const DEMARRAGE_MS = 20 * 1000;
export const INTERVALLE_VERIFICATION_MS = 30 * 60 * 1000;
const CLE_FILET = "animax-maj-filet";
const DELAI_FILET_MS = 10 * 1000;

/** Au retour dans l'app : peut-on recharger l'écran où se trouve l'enfant ? */
export function rechargementSur(route) {
  return !ROUTES_PROTEGEES.has(route?.name);
}

/** Nouvelle version prête juste après l'ouverture : recharger tout de suite ? */
export function rechargementAuDemarrage(route, depuisOuvertureMs) {
  return depuisOuvertureMs <= DEMARRAGE_MS && route?.name !== "histoire-route";
}

/** À une navigation : on recharge, sauf d'un chapitre à l'autre du lecteur. */
export function navigationSure(to, from) {
  return !(to?.name === "histoire-route" && from?.name === "histoire-route");
}

/** L'erreur est-elle celle d'un morceau de code introuvable (déploiement) ? */
export function estErreurDeChargement(err) {
  const m = String(err?.message || err || "");
  return /dynamically imported module|Importing a module script failed|Couldn't resolve component|Failed to fetch|Unable to preload CSS/i.test(m);
}

/** Anti-boucle du filet : au plus un rechargement forcé toutes les 10 s. */
export function filetAutorise(maintenant, dernier) {
  return !dernier || maintenant - Number(dernier) > DELAI_FILET_MS;
}

function rechargerVers(href) {
  let dernier = null;
  try { dernier = sessionStorage.getItem(CLE_FILET); } catch { /* navigation privée */ }
  if (!filetAutorise(Date.now(), dernier)) return false;
  try { sessionStorage.setItem(CLE_FILET, String(Date.now())); } catch { /* idem */ }
  window.location.assign(href);
  return true;
}

export function installerMiseAJour(router, { swUrl = `${import.meta.env.BASE_URL}sw.js` } = {}) {
  if (typeof window === "undefined") return null;

  // Filet, actif même sans service worker (1re visite, navigation privée).
  // Par le routeur, qui connaît l'écran DEMANDÉ : recharger l'adresse courante
  // ramenait l'enfant sur l'écran qu'il quittait (constaté au test).
  router.onError((err, to) => {
    if (estErreurDeChargement(err) && to) rechargerVers(router.resolve(to).href);
  });

  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return null;
  let prete = false;
  let enregistrement = null;
  // Une version précédente contrôlait déjà la page : un changement de
  // contrôleur est donc une MISE À JOUR. À la toute première installation,
  // rien à recharger.
  const dejaControlee = !!navigator.serviceWorker.controller;
  const ouverture = Date.now();
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!dejaControlee || prete) return;
    prete = true;
    if (rechargementAuDemarrage(router.currentRoute.value, Date.now() - ouverture)) {
      console.info("[maj] nouvelle version prête au démarrage : rechargement");
      window.location.reload();
      return;
    }
    console.info("[maj] nouvelle version prête, rechargement au prochain moment sûr");
  });
  navigator.serviceWorker.register(swUrl).then((r) => { enregistrement = r; }).catch(() => {});

  const verifier = () => {
    if (navigator.onLine === false || !enregistrement) return;
    enregistrement.update().catch(() => {});
  };
  setInterval(verifier, INTERVALLE_VERIFICATION_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (prete && rechargementSur(router.currentRoute.value)) {
      window.location.reload();
      return;
    }
    verifier();
  });

  router.beforeEach((to, from) => {
    if (!prete || !navigationSure(to, from)) return true;
    // Navigation complète vers la même destination : la nouvelle version
    // s'affiche directement sur l'écran demandé.
    window.location.assign(router.resolve(to).href);
    return false;
  });
  return { verifier, estPrete: () => prete };
}
